<script setup lang="ts">
import type { UserGatedAction } from '~/components/Login/LoginGatedActionInfo.vue';

export interface SignupModalProps {
    email?: string;
    action?: UserGatedAction;
}

// PROPS
const { email } = defineProps<SignupModalProps>();

// EVENTS
const emit = defineEmits(['close']);

// COMPOSABLES
const { tt } = useTypedI18n();
const { isMobile } = useResponsive();
const { trackSignupCheckEmailView } = useTracking();
const { closeSignupModal, openLoginModal } = useModals();

// DATA
const successfulSignup = ref(false);

// METHODS
const onSuccess = (): void => {
    trackSignupCheckEmailView(email);
    successfulSignup.value = true;
};

const onEditEmail = (): void => {
    emit('close', false);
    closeSignupModal();
    openLoginModal();
};

const onSuccessCta = (): void => {
    emit('close', true);
    closeSignupModal();
    openLoginModal();
};
</script>

<template>
    <UModal
        :fullscreen="isMobile"
        :title="successfulSignup ? tt('signup.success.title') : tt('signup.title')"
        :transition="!isMobile"
        v-bind="modalNoDescAriaFixupProps"
    >
        <slot />
        <template #body>
            <template v-if="successfulSignup">
                <div class="flex flex-col gap-6 text-center">
                    <BasePageTitle :sub-title="tt('signup.success.subtitle')" />
                    <UButton :label="tt('signup.success.cta')" size="xl" block @click="onSuccessCta" />
                </div>
            </template>

            <template v-else>
                <LoginGatedActionInfo v-if="action" class="mb-4" :action="action" />
                <BasePageTitle v-else class="text-center" :sub-title="tt('signup.subtitle')" />
                <SignupForm :email="email" @edit-email="onEditEmail" @success="onSuccess" />
            </template>
        </template>
    </UModal>
</template>
